import { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import textActions from "actions/text";
import TextingComponents from "../TextingComponents";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { Segment, Icon, Header } from "semantic-ui-react";
import { Notification, STATUS_TYPES } from "dyl-components";
import selfReportActions from 'actions/self_report';
import { convertRecipientAccountVariables, convertRecipientContactVariables } from "utils/TextFormUtils";
import { PRODUCT_PERMISSIONS, productRestriction } from 'dyl-components';
import { ActivityPopup } from "shared/forms/NewActivity";
import { TextingComponentContext } from "shared/context/TextingComponentProvider";
import CustomPrompt from "shared/confirmation/CustomPrompt";
import { useConfirm } from "shared/confirmation/useConfirm";

const LIMIT = 25;

function TextTab() {
    const dispatch = useDispatch();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const page = Number(params.get('page')) || 1;
    const { contact_id, account_id } = useParams();
    const recipientId = contact_id || account_id;
    const textPhones = useSelector(state => state.text.phones || []);
    const productPermissions = useSelector(state => state.subscription.productPermissions);
    const hasText = productRestriction(PRODUCT_PERMISSIONS.TEXT_VIEW, productPermissions);
    const [selectedTextActivityLog, setSelectedTextActivityLog] = useState(null);
    const contactPhones = useSelector(state => state.contact_phone.phone);
    const { isTextFormDirty } = useContext(TextingComponentContext);
    const { isConfirmed } = useConfirm();

    const loadConversations = useCallback(() => {
        const fromPhoneQuery = params.get('from')?.split(",") || [];
        const fromPhones = fromPhoneQuery?.length ? fromPhoneQuery : textPhones || [];
        const toPhoneQuery = params.get('to')?.split(",") || [];
        const toPhones = toPhoneQuery?.length ? toPhoneQuery : contactPhones?.map(phone => phone.phone) || [];
        if (hasText && toPhones.length) {
            dispatch(
                textActions.getConversations({
                    contact_phone: toPhones,
                    page: params.get('page') || 1,
                    limit: LIMIT,
                    customer_phone: fromPhones
                })
            );
        } else {
            dispatch(
                selfReportActions.readUserTextActivity({ page: params.get('page') || 1, limit: LIMIT })
            );
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps        
    }, [dispatch, params]);

    useEffect(() => {
        loadConversations();
        if (hasText) {
            const fromPhoneQuery = params.get('from')?.split(",") || [];
            const fromPhones = fromPhoneQuery?.length ? fromPhoneQuery : textPhones || [];
            const toPhoneQuery = params.get('to')?.split(",") || [];
            const toPhones = toPhoneQuery?.length ? toPhoneQuery : contactPhones?.map(phone => phone.phone) || [];
            if (fromPhones.length === 1 && toPhones.length === 1) {
                dispatch(textActions.getFilteredMessages({ contact_phone: toPhones[0], customer_phone: fromPhones[0] }));
                dispatch(textActions.setConversationBeingViewedInTextTab({ contact_phone: toPhones[0], customer_phone: fromPhones[0] }));
            } else {
                const contact_phone = contactPhones?.map(phone => phone.phone)[0];
                const customer_phone = textPhones[0];
                if (contact_phone && customer_phone) {
                    dispatch(textActions.getFilteredMessages({ contact_phone, customer_phone }));
                    dispatch(textActions.setConversationBeingViewedInTextTab({ contact_phone, customer_phone }));
                }
            }
        }
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadConversations, dispatch]);

    const { conversations, conversations_count = 0, isReadingConversations } = useSelector(state => state.text);
    
    const textTabConversationRecipient = useSelector(state => state.text.textTabConversationRecipient);
    const textTabConversationSender = useSelector(state => state.text.textTabConversationSender);
    const idOfTextLogBeingViewed = useSelector(state => state.text.textTabLogId);
    const { userTextLogs = [], isReadingUserTextLogs } = useSelector(
        state => state.self_report
    );
    const textTabConversationBeingViewed = hasText ? conversations.find(conversation => conversation.contact_phone === textTabConversationRecipient && conversation.customer_phone === textTabConversationSender)?.id : userTextLogs.find(log => log.id === idOfTextLogBeingViewed)?.id;
    const { filteredMessages = [], isReadingMessages } = useSelector(state => state.text);
    const recipientType = (() => {
        if (account_id) return 'account';
        if (contact_id) return 'contact';
        return '';
    })();

    const onPageChange = (_, { activePage }) => {
        const query = new URLSearchParams(params);
        query.set('page', activePage);
        navigate(`/${recipientType}/${recipientId}/texts?${query.toString()}`);
    };

    const onRowClick = async (conversation) => {
        const { contact_phone, customer_phone } = conversation;
        dispatch(textActions.setConversationBeingViewedInTextTab(hasText ? { contact_phone, customer_phone } : { log_id: conversation.id }));
        try {
            if (hasText) {
                await dispatch(textActions.getFilteredMessages({ contact_phone, customer_phone, contact_id:recipientId }));
            }else{
                setSelectedTextActivityLog(conversation?.messages)
            }
        } catch (error) {
            Notification.alert("Something went wrong", STATUS_TYPES.ERROR);
        }
    };

    const contact = useSelector(state => state.contact.contact);
    const recipientName = useSelector(state => {
        if (recipientType === 'account') {
            return state.account.account?.name;
        }
        if (recipientType === 'contact') {
            return `${contact?.first_name} ${contact?.last_name}`;
        }
        return '';
    })
    const recipientPhone = useSelector(state => {
        const mainPhone = recipientType === 'account'? state.account.account?.phones?.find((item) => item.main) : state.contact.phone.find((item) => item.main);
        return mainPhone?.phone;
    })

    const isLoadingRecipientDetails = useSelector(state => state.contact.isReadingContact || state.account.isLoadingAccountDetails);
    const [filters, setFilters] = useState({});

    const userOptions = useSelector(state => state.text?.userOptions || []);

    const contactVariableValues = useSelector(state => state.contact);
    const account = useSelector(state => state.account.account);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const onFilter = async (_, { name, value }) => {
        setFilters({
            ...filters,
            [name]: value
        });
    }

    useEffect(() => {
        const query = new URLSearchParams(params);
        if (filters.from?.length) {
            query.set('from', filters.from);
        } else {
            query.delete('from');
        }
        if (filters.to?.length) {
            query.set('to', filters.to);
        } else {
            query.delete('to');
        }
        query.set('page', 1);
        navigate(`/${recipientType}/${recipientId}/texts?${query.toString()}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const toDisplay = (() => {
        if (hasText) {
            return conversations;
        }
        return userTextLogs.map((textLog)=>({
            ...textLog,
            message_ts: textLog.created,
            last_message: {
                message: textLog.messages,
            },
            contact_name: recipientName,
            contact_phone: recipientPhone
        }));
    })();

    CustomPrompt(null, isTextFormDirty, isConfirmed, 'Text will not be saved.', 'Are you sure you want to exit?');

    return (
        <>
            <Header className="ActivitiesTab__Header" as="h2">
               <ActivityPopup
                    trigger={
                        <Icon
                            className="TimelineTab__add"
                            name="plus"
                            color="blue"
                            link
                            circular
                            inverted
                            size="tiny"
                            disabled={hasText}
                            onClick={() => setIsAddModalOpen(true)}
                        />
                    }
                    activityProps={{
                        hideActivitySelection: true,
                        account: { id: account_id },
                        textOnly: true
                    }}
                    open={isAddModalOpen}
                    onOpen={() => setIsAddModalOpen(true)}
                    onClose={() => setIsAddModalOpen(false)}
                /> Text
            </Header>
            <TextingComponents
                filteredMessages={filteredMessages}
                isReadingFilteredMessages={isReadingMessages}
                messages={toDisplay}
                messagesCount={hasText ? conversations_count : userTextLogs.length}
                loading={isReadingConversations || isReadingUserTextLogs}
                onPageChange={onPageChange}
                activePage={page}
                onRowClick={onRowClick}
                recipientId={recipientId}
                recipientName={recipientName}
                recipientOptions={contactPhones}
                isLoadingRecipientDetails={isLoadingRecipientDetails}
                onFilter={onFilter}
                selectedRecipientFilters={params.get('to')?.split(",") || []}
                selectedSenderFilters={params.get('from')?.split(",") || []}
                selectedRow={textTabConversationBeingViewed}
                userOptions={userOptions}
                onReadMessages={(contact_phone, customer_phone) => {
                    loadConversations();
                    if (hasText) {
                        dispatch(textActions.getFilteredMessages({ contact_phone, customer_phone }));
                        dispatch(textActions.setConversationBeingViewedInTextTab({ contact_phone, customer_phone }));
                    }
                }}
                onReplaceRecipientVariable={(variable) => {
                    if (recipientType === 'contact') {
                        return convertRecipientContactVariables(variable, contactVariableValues);
                    }
                    if (recipientType === 'account') {
                        return convertRecipientAccountVariables(variable, account)
                    }
                    return variable;
                }}
                isAccount={recipientType === 'account'}
                contact_phone={textTabConversationRecipient}
                customer_phone={textTabConversationSender}
                onChangeNumbers={(payload) => {
                    dispatch(textActions.setConversationBeingViewedInTextTab(payload));
                    dispatch(textActions.getFilteredMessages(payload));
                }}
                recipientFilterOptions={contactPhones}
                selectedTextActivityLog={selectedTextActivityLog}
            />
        </>
    );
}

const TextTabContainer = () => {
    const isReadingPhones = useSelector(state => state.contact_phone.isReadingPhones);

    if (isReadingPhones) {
        return <Segment basic loading />
    }

    return <TextTab />
}

export default TextTabContainer;
